/**
 * Dutch (Flemish) numbers, money, distances and dates for Bezettingsgraad: a thousands dot, a decimal comma, a real
 * minus sign, whole percentages without a space ("78%"), averages with one decimal ("82,2"), amounts in full
 * ("€62.700", "€4,73 miljoen"), never k or mln.
 *
 * round() is the one rounding helper, half away from zero after cutting float noise, exactly as the analysis
 * rounds. Every printed number, in a sentence or on a chart, goes through it, so a sentence and its chart never
 * differ by one.
 *
 * Dates in the snapshot are ISO strings ('YYYY-MM-DD') in Europe/Brussels and are formatted from their parts, never
 * through a Date in the browser's own time zone. Datetimes ('...T05:17:20Z') are shown in Europe/Brussels.
 */

/** A number that may be missing: every formatter prints '' for anything that is not a finite number. */
export type Num = number | null | undefined;

export const MONTHS = Object.freeze([
  "jan",
  "feb",
  "mrt",
  "apr",
  "mei",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
]);
export const MONTH_NAMES = Object.freeze([
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
]);
export const WEEKDAYS = Object.freeze(["ma", "di", "wo", "do", "vr", "za", "zo"]);
export const MINUS = "−";
export const TIME_ZONE = "Europe/Brussels";
/** A share on fewer cases shows its count next to the percentage and is never highlighted. */
export const SMALL = 30;

export const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** The shared rounding: half away from zero after cutting float noise (0,285 × 100 is 28,5 and rounds to 29). */
export function round(value: number, digits = 0): number {
  if (!isNum(value)) {
    return value;
  }
  const factor = 10 ** digits;
  const scaled = Number((Math.abs(value) * factor).toPrecision(12));
  return (Math.sign(value) * Math.round(scaled)) / factor;
}
export const roundHalfUp = round;

/** A share as percentage points, rounded as printed: pct(0.785) is 79. */
export function pct(share: Num, digits = 0): number | null {
  return isNum(share) ? round(share * 100, digits) : null;
}

/** A number the Flemish way: getal(2672) is '2.672', getal(1.51, 1) is '1,5', getal(-3) is '−3'. '' for no number. */
export function getal(
  value: Num,
  digits = 0,
  { signed = false }: { signed?: boolean } = {},
): string {
  if (!isNum(value)) {
    return "";
  }
  const rounded = round(value, Math.max(0, digits));
  const [whole = "", frac] = Math.abs(rounded).toFixed(Math.max(0, digits)).split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const text = frac ? `${grouped},${frac}` : grouped;
  if (rounded < 0 && /[1-9]/.test(text)) {
    return `${MINUS}${text}`;
  }
  return signed && rounded > 0 ? `+${text}` : text;
}

/** An average with one decimal: gem(82.24) is '82,2'. */
export function gem(value: Num): string {
  return getal(value, 1);
}

/** A share (0 to 1) as a percentage without a space: procent(0.78) is '78%'. A zero never carries a sign. */
export function procent(
  share: Num,
  digits = 0,
  { signed = false }: { signed?: boolean } = {},
): string {
  if (!isNum(share)) {
    return "";
  }
  const p = round(share * 100, digits);
  if (p === 0) {
    return `${getal(0, digits)}%`;
  }
  return `${getal(p, digits, { signed })}%`;
}

export interface ShareOptions {
  readonly min?: number;
  readonly digits?: number;
  readonly noun?: string;
}

/**
 * A share is always a percentage: never a fraction, and never a switch between the two in one chart or table. On
 * fewer than 30 cases its count follows, so the reader sees how thin it is: procentVan(0.333, 12) is '33% van 12',
 * with {noun: 'werven'} '33% van 12 werven'. On 30 or more it is '33%'.
 */
export function procentVan(
  share: Num,
  total: Num,
  { min = SMALL, digits = 0, noun = "" }: ShareOptions = {},
): string {
  if (!isNum(share)) {
    return "";
  }
  const text = procent(share, digits);
  if (!isNum(total) || total >= min) {
    return text;
  }
  return `${text} van ${getal(total)}${noun ? ` ${noun}` : ""}`;
}

/** A share of part in total as procentVan prints it: aandeel(4, 12) is '33% van 12', aandeel(40, 120) is '33%'. */
export function aandeel(part: Num, total: Num, opts: ShareOptions = {}): string {
  if (!isNum(part) || !isNum(total) || total <= 0) {
    return "";
  }
  return procentVan(part / total, total, opts);
}

/** Whether a share rests on fewer than 30 cases: its count then shows next to it, and it is never highlighted. */
export function isSmall(total: Num, min = SMALL): boolean {
  return !isNum(total) || total < min;
}

/**
 * The precision an amount is shown at, through round(): to the ten below €1.000, to the hundred below €100.000 and
 * to the thousand above, so no amount carries a false precision of single euros ('€158.000', '€7.300', '€960').
 */
export function euroRound(value: number): number {
  if (!isNum(value)) {
    return value;
  }
  const size = Math.abs(value);
  const digits = round(size, -3) >= 1e5 ? -3 : round(size, -2) >= 1e3 ? -2 : -1;
  return round(value, digits);
}

/**
 * An amount in full: '€62.700', '€950', '€4,73 miljoen' from one million. With {miljoen: true} always in millions
 * ('€0,81 miljoen'), for a chart whose other labels are in millions. Excl. VAT is the page's to say.
 */
export function euro(value: Num, { miljoen = false }: { miljoen?: boolean } = {}): string {
  if (!isNum(value)) {
    return "";
  }
  const size = Math.abs(value);
  if (miljoen || round(size, -4) >= 1e6) {
    const m = round(size / 1e6, 2);
    return m === 0 ? "€0" : `${value < 0 ? MINUS : ""}€${getal(m, 2)} miljoen`;
  }
  const r = euroRound(value);
  return r === 0 ? "€0" : `${r < 0 ? MINUS : ""}€${getal(Math.abs(r), 0)}`;
}

/** A signed amount for a change: '+€26.000', '−€38.000', '€0' (opts as euro()). */
export function euroSigned(value: Num, opts: { miljoen?: boolean } = {}): string {
  if (!isNum(value)) {
    return "";
  }
  const text = euro(value, opts);
  if (text === "€0") {
    return text;
  }
  return value > 0 ? `+${text}` : text;
}

/** A distance: km(23.4) is '23 km'. */
export function km(value: Num, digits = 0): string {
  return isNum(value) ? `${getal(value, digits)} km` : "";
}

/** A count with its noun: aantal(1, 'kraan', 'kranen') is '1 kraan'. Plural branches for sentences live in nl.ts. */
export function aantal(n: number, singular: string, plural: string): string {
  return `${getal(n)} ${round(n) === 1 ? singular : plural}`;
}

/** Days with their noun: '1 dag', '12 dagen'. */
export function dagen(n: Num): string {
  return isNum(n) ? aantal(n, "dag", "dagen") : "";
}

/** A Dutch list: ['a', 'b', 'c'] is 'a, b en c'. With word 'of': 'a, b of c'. */
export function lijst(items: readonly (string | null | undefined)[], word = "en"): string {
  const parts = items.filter((x): x is string => x !== "" && x != null);
  if (parts.length <= 1) {
    return parts.join("");
  }
  return `${parts.slice(0, -1).join(", ")} ${word} ${parts.at(-1) ?? ""}`;
}

// ---------------------------------------------------------------- dates

/** An ISO date or month as a string that may be missing. */
export type Iso = string | null | undefined;

export interface DateParts {
  readonly y: number;
  readonly m: number;
  readonly d: number;
}

/** The parts of an ISO date or month: parseIso('2026-10-21') is {y: 2026, m: 10, d: 21}. */
export function parseIso(iso: unknown): DateParts | null {
  const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(String(iso ?? ""));
  if (!match) {
    return null;
  }
  return { y: Number(match[1]), m: Number(match[2]), d: match[3] ? Number(match[3]) : 1 };
}

const utc = (iso: Iso): number => {
  const p = parseIso(iso);
  return p ? Date.UTC(p.y, p.m - 1, p.d) : NaN;
};
const isoOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
const monthName = (m: number): string => MONTHS[m - 1] ?? "";
const monthFull = (m: number): string => MONTH_NAMES[m - 1] ?? "";

/** Date arithmetic on ISO dates, in whole calendar days. */
export const dates = Object.freeze({
  ms: utc,
  fromMs: isoOf,
  addDays: (iso: Iso, n: number): string => isoOf(utc(iso) + n * 86400000),
  daysBetween: (fromIso: Iso, toIso: Iso): number =>
    Math.round((utc(toIso) - utc(fromIso)) / 86400000),
  /** 0 is Monday */
  weekday: (iso: Iso): number => (new Date(utc(iso)).getUTCDay() + 6) % 7,
  compare: (a: Iso, b: Iso): number => utc(a) - utc(b),
  monday: (iso: Iso): string =>
    isoOf(utc(iso) - ((new Date(utc(iso)).getUTCDay() + 6) % 7) * 86400000),
  year: (iso: Iso): number | null => parseIso(iso)?.y ?? null,
});

/** '21 okt 2026'. */
export function datum(iso: Iso): string {
  const p = parseIso(iso);
  return p ? `${p.d} ${monthName(p.m)} ${p.y}` : "";
}

/** '26 okt', without the year (labels, week columns). */
export function dagMaand(iso: Iso): string {
  const p = parseIso(iso);
  return p ? `${p.d} ${monthName(p.m)}` : "";
}

/** '21 oktober 2026', for sentences. */
export function datumVol(iso: Iso): string {
  const p = parseIso(iso);
  return p ? `${p.d} ${monthFull(p.m)} ${p.y}` : "";
}

/** '21 oktober', for sentences that name the year elsewhere. */
export function dagMaandVol(iso: Iso): string {
  const p = parseIso(iso);
  return p ? `${p.d} ${monthFull(p.m)}` : "";
}

function brusselsParts(when: Date, opts: Intl.DateTimeFormatOptions): Record<string, string> {
  return Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", { timeZone: TIME_ZONE, ...opts })
      .formatToParts(when)
      .map((part) => [part.type, part.value]),
  );
}

/** A datetime in Europe/Brussels: '21 okt 2026, 07:17'. */
export function datumTijd(isoDatetime: Iso): string {
  const when = new Date(isoDatetime ?? "");
  if (Number.isNaN(when.getTime())) {
    return "";
  }
  const parts = brusselsParts(when, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  return `${Number(parts.day)} ${monthName(Number(parts.month))} ${parts.year}, ${parts.hour}:${parts.minute}`;
}

/**
 * The calendar date ('YYYY-MM-DD') of a moment in Europe/Brussels: the real today of the reader for
 * vandaag(new Date()), as the meldingen need it (a read melding hides from the next day on), not the snapshot's
 * as_of.
 */
export function vandaag(when: Date | string | number = new Date()): string {
  const d = when instanceof Date ? when : new Date(when);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const parts = brusselsParts(d, { year: "numeric", month: "2-digit", day: "2-digit" });
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** The Brussels calendar date of an ISO datetime ('2026-10-25T21:30:00Z' is '2026-10-25'). A plain date passes. */
export function dagVan(isoDatetime: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoDatetime)) {
    return isoDatetime;
  }
  return vandaag(new Date(isoDatetime));
}

/** A count against its whole for a hover row: vanDe(84, 106) is '84 van 106'. */
export function vanDe(part: Num, whole: Num): string {
  return isNum(part) && isNum(whole) ? `${getal(part)} van ${getal(whole)}` : "";
}

/** '21 oktober 2026 om 07:17', for sentences. */
export function datumTijdVol(isoDatetime: Iso): string {
  const text = datumTijd(isoDatetime);
  if (!text) {
    return "";
  }
  const [d = "", time = ""] = text.split(", ");
  const [day = "", mon = "", year = ""] = d.split(" ");
  return `${day} ${monthFull(MONTHS.indexOf(mon) + 1)} ${year} om ${time}`;
}

/** A time of day in Europe/Brussels: '10:42'. */
export function tijd(isoDatetime: Iso): string {
  const text = datumTijd(isoDatetime);
  return text ? (text.split(", ")[1] ?? "") : "";
}

/** 'okt 2026', from 'YYYY-MM' or a date. */
export function maand(ym: Iso): string {
  const p = parseIso(ym);
  return p ? `${monthName(p.m)} ${p.y}` : "";
}

/** 'okt', for month labels on an axis. */
export function maandKort(ym: Iso): string {
  const p = parseIso(ym);
  return p ? monthName(p.m) : "";
}

/** 'oktober 2026', for sentences. */
export function maandVol(ym: Iso): string {
  const p = parseIso(ym);
  return p ? `${monthFull(p.m)} ${p.y}` : "";
}

/** 'oktober'. */
export function maandNaam(ym: Iso): string {
  const p = parseIso(ym);
  return p ? monthFull(p.m) : "";
}

/** An ISO week as a label: week('2026-W44') is 'wk 44'. */
export function week(isoWeek: Iso): string {
  const match = /W(\d{1,2})$/.exec(String(isoWeek ?? ""));
  return match ? `wk ${Number(match[1])}` : "";
}

/** The number of an ISO week: weekNr('2026-W44') is 44. */
export function weekNr(isoWeek: Iso): number | null {
  const match = /W(\d{1,2})$/.exec(String(isoWeek ?? ""));
  return match ? Number(match[1]) : null;
}

/** A span of days: '21 okt tot 23 okt 2026' within a year, else with both years. */
export function periode(from: Iso, to: Iso): string {
  const a = parseIso(from);
  const b = parseIso(to);
  if (!a || !b) {
    return "";
  }
  return a.y === b.y ? `${dagMaand(from)} tot ${datum(to)}` : `${datum(from)} tot ${datum(to)}`;
}

/**
 * A span of days in short form, for a button, a legend or a column: "21 okt 2026", "1 – 30 sep 2026",
 * "1 jan – 30 sep 2026", "3 aug 2025 – 2 feb 2026".
 */
export function spanKort(from: Iso, to: Iso): string {
  const a = parseIso(from);
  const b = parseIso(to);
  if (!a || !b) {
    return "";
  }
  if (from === to) {
    return datum(from);
  }
  if (a.y === b.y && a.m === b.m) {
    return `${a.d} – ${datum(to)}`;
  }
  return a.y === b.y ? `${dagMaand(from)} – ${datum(to)}` : `${datum(from)} – ${datum(to)}`;
}
